import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, ListItemIcon, List, ListItem, ListItemText, Tabs, Tab, Button, Link,
  Alert, Chip, Divider, Card, Stack, Tooltip
} from '@mui/material';
import { darken ,lighten, alpha} from '@mui/material/styles';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShieldIcon from '@mui/icons-material/Shield';
import MemoryIcon from '@mui/icons-material/Memory';
import RadarIcon from '@mui/icons-material/Radar';
import BugReportIcon from '@mui/icons-material/BugReport';
import KeyIcon from '@mui/icons-material/Key';
import ReportIcon from '@mui/icons-material/Assignment';
import CheckIcon from '@mui/icons-material/Check';
import { keyframes } from '@mui/system';

import { setToken } from '../../components/jwtToken';
import { BASEURL,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import ForgotPasswordModal from '../../components/login/forgotPasswordModel';
import LoginForm from '../../components/login/loginForm';
import MfaVerificationForm from '../../components/login/MfaVerificationForm';
import SignupForm from '../../components/login/signupForm';
import { SendSignupLink } from '../../services/signup/signupService';
import { ResendLoginMfa, SendLogin, VerifyLoginMfa } from '../../services/login/loginService';
import {
  buildMfaChallenge,
  clearMfaChallenge,
  loadMfaChallenge,
  saveMfaChallenge,
} from '../../services/login/mfaChallenge';
import { SendForgotPasswordLink } from '../../services/forgotPassword/passwordService';

function LinkTab(props) {
  return (
    <Tab
      component="a"
      onClick={(event) => event.preventDefault()}
      {...props}
    />
  );
}

// ——— animations ———
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 ${PRIMARYCOLOR}; }
  70% { box-shadow: 0 0 0 14px ${alpha(PRIMARYCOLOR, 0)}; }
  100% { box-shadow: 0 0 0 0 ${alpha(PRIMARYCOLOR, 0)}; }
`;

const drift = keyframes`
  0% { transform: translateY(0); opacity: .35; }
  50% { transform: translateY(-8px); opacity: .65; }
  100% { transform: translateY(0); opacity: .35; }
`;

const glowSweep = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(120%); }
`;

export default function Login() {
  const [value, setValue] = React.useState(0);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isSignUp, setIsSignUp] = React.useState(false);
  const [openModal, setOpenModal] = React.useState(false);
  const [alert, setAlert] = React.useState(null);
  const [authStage, setAuthStage] = React.useState('credentials');
  const [mfaChallenge, setMfaChallenge] = React.useState(null);
  const [mfaCode, setMfaCode] = React.useState('');
  const [mfaError, setMfaError] = React.useState('');
  const [loginLoading, setLoginLoading] = React.useState(false);
  const [mfaVerifying, setMfaVerifying] = React.useState(false);
  const [mfaResending, setMfaResending] = React.useState(false);
  const [now, setNow] = React.useState(Date.now());
  const navigate = useNavigate();
  const alertTimeoutRef = React.useRef(null);

  React.useEffect(() => {
    sessionStorage.removeItem('token');
    const storedChallenge = loadMfaChallenge();
    if (storedChallenge) {
      setMfaChallenge(storedChallenge);
      setAuthStage('mfa');
      setNow(Date.now());
    }

    return () => {
      if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (authStage !== 'mfa') return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [authStage, mfaChallenge?.challengeToken]);

  React.useEffect(() => {
    if (authStage !== 'mfa' || !mfaChallenge?.expiresAt) return undefined;

    const remaining = mfaChallenge.expiresAt - Date.now();
    const expireChallenge = () => {
      clearMfaChallenge();
      setMfaChallenge(null);
      setMfaCode('');
      setMfaError('');
      setPassword('');
      setAuthStage('credentials');
      setAlert({ type: 'error', message: 'Your verification code expired. Please sign in again.' });
    };

    if (remaining <= 0) {
      expireChallenge();
      return undefined;
    }

    const timer = setTimeout(expireChallenge, remaining);
    return () => clearTimeout(timer);
  }, [authStage, mfaChallenge?.expiresAt]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setIsSignUp(newValue === 1);
  };

  const handleAlert = (type, message) => {
    setAlert({ type, message });
    if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
    alertTimeoutRef.current = setTimeout(() => setAlert(null), 5000);
  };

  const completeLogin = (response) => {
    clearMfaChallenge();
    setToken(response.token);
    if (response.onboarding_completed === 0) navigate('/onboarding');
    else navigate('/pentests');
  };

  const beginMfaVerification = (response) => {
    const challenge = buildMfaChallenge(response);
    if (!challenge) {
      handleAlert('error', 'Unable to start email verification. Please try again.');
      return;
    }

    saveMfaChallenge(challenge);
    setMfaChallenge(challenge);
    setMfaCode('');
    setMfaError('');
    setAlert(null);
    setNow(Date.now());
    setAuthStage('mfa');
  };

  const returnToLogin = (message) => {
    clearMfaChallenge();
    setMfaChallenge(null);
    setMfaCode('');
    setMfaError('');
    setPassword('');
    setAuthStage('credentials');
    if (message) handleAlert('error', message);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loginLoading) return;

    setLoginLoading(true);
    setAlert(null);
    try {
      const response = await SendLogin(email.trim(), password);
      if (response?.error || !response?.success) {
        handleAlert('error', response?.message || 'Login failed. Please check your credentials and try again.');
        return;
      }

      if (response.mfa_required === false && response.token) {
        completeLogin(response);
        return;
      }

      if (response.mfa_required === true && response.challenge_token) {
        beginMfaVerification(response);
        return;
      }

      handleAlert('error', 'The login response was incomplete. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    if (mfaVerifying || mfaCode.length !== 6 || !mfaChallenge?.challengeToken) return;

    setMfaVerifying(true);
    setMfaError('');
    try {
      const response = await VerifyLoginMfa(mfaChallenge.challengeToken, mfaCode);
      if (!response?.error && response?.success && response?.token) {
        completeLogin(response);
        return;
      }

      const message = response?.message || 'Unable to verify that code. Please try again.';
      const normalizedMessage = message.toLowerCase();
      if (normalizedMessage.includes('verification challenge')) {
        returnToLogin(message);
        return;
      }

      if (!normalizedMessage.includes('verification code')) {
        setMfaError(message);
        return;
      }

      const failedAttempts = Number(mfaChallenge.failedAttempts || 0) + 1;
      if (failedAttempts >= 5) {
        returnToLogin('Too many incorrect verification attempts. Please sign in again.');
        return;
      }

      const updatedChallenge = { ...mfaChallenge, failedAttempts };
      saveMfaChallenge(updatedChallenge);
      setMfaChallenge(updatedChallenge);
      setMfaCode('');
      setMfaError(message);
    } finally {
      setMfaVerifying(false);
    }
  };

  const handleMfaResend = async () => {
    if (mfaResending || !mfaChallenge?.challengeToken) return;
    if (mfaChallenge.resendAvailableAt > Date.now()) return;

    setMfaResending(true);
    setMfaError('');
    try {
      const response = await ResendLoginMfa(mfaChallenge.challengeToken);
      if (!response?.error && response?.success && response?.challenge_token) {
        const replacementChallenge = buildMfaChallenge(response);
        saveMfaChallenge(replacementChallenge);
        setMfaChallenge(replacementChallenge);
        setMfaCode('');
        setNow(Date.now());
        return;
      }

      const message = response?.message || 'Unable to resend a verification code. Please try again.';
      const normalizedMessage = message.toLowerCase();
      if (normalizedMessage.includes('verification challenge')) {
        returnToLogin(message);
        return;
      }

      const waitMatch = message.match(/wait\s+(\d+)\s+seconds?/i);
      if (waitMatch) {
        const updatedChallenge = {
          ...mfaChallenge,
          resendAvailableAt: Date.now() + Number(waitMatch[1]) * 1000,
        };
        saveMfaChallenge(updatedChallenge);
        setMfaChallenge(updatedChallenge);
        setNow(Date.now());
      }
      setMfaError(message);
    } finally {
      setMfaResending(false);
    }
  };

  const mfaExpiresIn = mfaChallenge
    ? Math.max(0, Math.ceil((mfaChallenge.expiresAt - now) / 1000))
    : 0;
  const resendAvailableIn = mfaChallenge
    ? Math.max(0, Math.ceil((mfaChallenge.resendAvailableAt - now) / 1000))
    : 0;

  const handleSignup = async (e) => {
    e.preventDefault();
    const response = await SendSignupLink(email);
    if (response.success) handleAlert('success', 'Sign-up email sent successfully!');
    else handleAlert('error', 'Failed to send sign-up email. Please try again.');
  };

  const handleModalOpen = () => setOpenModal(true);
  const handleModalClose = () => setOpenModal(false);

  const handleForgotPassword = async () => {
    const response = await SendForgotPasswordLink(email);
    if (response.success) handleModalClose();
    else console.log('Error sending reset link');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        // layered, futuristic background (white/blue brand)
        background:
          `radial-gradient(1200px 600px at 10% -10%, ${alpha(PRIMARYCOLOR, 0.18)}, transparent 60%), radial-gradient(1000px 500px at 110% 20%, ${alpha(PRIMARYCOLOR, 0.10)}, transparent 60%), linear-gradient(180deg, #ffffff 0%, ${lighten(PRIMARYCOLOR, 0.92)} 85%)`,
        '&:before': {
          // subtle scanline grid
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage:
            `linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px),
             linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)`,
          backgroundSize: '36px 36px, 36px 36px',
          pointerEvents: 'none',
          mixBlendMode: 'multiply'
        }
      }}
    >
      {/* floating nodes for depth */}
      {[...Array(6)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: 12 + (i % 3) * 4,
            height: 12 + (i % 3) * 4,
            borderRadius: '50%',
            backgroundColor: alpha(PRIMARYCOLOR, 0.25),
            left: `${10 + i * 12}%`,
            top: `${20 + (i % 4) * 15}%`,
            animation: `${drift} ${6 + i}s ease-in-out infinite`,
            filter: 'blur(0.2px)'
          }}
        />
      ))}

      {/* center card */}
      <Card
        elevation={0}
        sx={{
          width: 'min(1200px, 92%)',
          mx: 'auto',
          mt: { xs: 6, md: 10 },
          mb: { xs: 6, md: 10 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${alpha(PRIMARYCOLOR, 0.25)}`,
          background: 'linear-gradient(180deg, rgba(255,255,255,.92), rgba(255,255,255,.86))',
          boxShadow: `0 20px 60px ${alpha(PRIMARYCOLOR, 0.25)}`
        }}
      >
        {/* LEFT: brand / value prop */}
        <Box
          sx={{
            position: 'relative',
            p: { xs: 4, md: 5 },
            background:
              `linear-gradient(160deg, ${PRIMARYCOLOR} 0%, ${darken(PRIMARYCOLOR, 0.18)} 60%, ${darken(PRIMARYCOLOR, 0.36)} 100%)`,
            color: '#fff'
          }}
        >
          {/* sweeping glow accent */}
          <Box
            sx={{
              position: 'absolute',
              top: 0, left: 0, height: '100%', width: '100%',
              overflow: 'hidden', pointerEvents: 'none',
              '&:after': {
                content: '""',
                position: 'absolute',
                top: 0, left: 0, height: '100%', width: '40%',
                transform: 'translateX(-120%)',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.18), transparent)',
                animation: `${glowSweep} 6s ease-in-out infinite`
              }
            }}
          />

          <Stack spacing={2}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Box
                sx={{
                  width: 12, height: 12, borderRadius: '50%',
                  bgcolor: '#fff', animation: `${pulse} 2.4s ease-out infinite`
                }}
              />
              <Typography variant="overline" sx={{ letterSpacing: 3, opacity: 0.9 }}>
                {COMPANY}
              </Typography>
            </Stack>

            <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              A Fleet of AI Pentesting Agents
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.95 }}>
              External • API • Web • Vishing • Dark Web Monitoring • Auto-Written Reports
            </Typography>

            {/* Agent badges */}
            <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap sx={{ mt: 1 }}>
              <Chip icon={<RadarIcon sx={{ color: 'inherit !important' }} />} label="External Agent" sx={chipWhite()} />
              <Chip icon={<MemoryIcon sx={{ color: 'inherit !important' }} />} label="API Agent" sx={chipWhite()} />
              <Chip icon={<BugReportIcon sx={{ color: 'inherit !important' }} />} label="Web Agent" sx={chipWhite()} />
              <Chip icon={<KeyIcon sx={{ color: 'inherit !important' }} />} label="Vishing Agent" sx={chipWhite()} />
              <Chip icon={<ShieldIcon sx={{ color: 'inherit !important' }} />} label="Darkweb Monitor" sx={chipWhite()} />
              <Chip icon={<ReportIcon sx={{ color: 'inherit !important' }} />} label="Compliance Reports" sx={chipWhite()} />
            </Stack>

            <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.25) }} />

            {/* Compliance chips */}
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {['SOC 2', 'PCI', 'HIPAA', 'NIST', 'ISO 27001'].map((tag) => (
                <Chip key={tag} size="small" label={tag} sx={chipTranslucent()} />
              ))}
            </Stack>

            {/* Proof bullets */}
            <List dense sx={{ mt: 1, color: 'rgba(255,255,255,.95)' }}>
              {[
                'Autonomous reconnaissance, exploitation & reporting',
                'Compliance-ready pentest write-ups',
                'Blueprinted attack chains & threat models'
              ].map((t) => (
                <ListItem key={t} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28, color: '#fff' }}>
                    <CheckCircleOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ sx: { fontSize: 14 } }} primary={t} />
                </ListItem>
              ))}
            </List>
            {/*}
            <Box sx={{ mt: 1 }}>
              <Tooltip title="Learn how StealthNet AI orchestrates multi-agent pentests" placement="right">
                <Button
                  variant="outlined"
                  sx={{
                    color: '#fff',
                    borderColor: alpha('#fff', 0.35),
                    ':hover': { borderColor: '#fff', backgroundColor: alpha('#fff', 0.08) },
                    borderRadius: 999,
                    px: 2.2, py: 0.8
                  }}
                  onClick={() => window.open('https://stealthnet.ai', '_blank')}
                >
                  Explore the Platform
                </Button>
              </Tooltip>
            </Box>
            */}
          </Stack>
        </Box>
        

        {/* RIGHT: auth card */}
        <Box
          sx={{
            p: { xs: 3.5, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {/* top title */}
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0e2b5c' }}>
            {authStage === 'mfa' ? 'Check your email' : 'Sign in to your Command Console'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {authStage === 'mfa'
              ? 'Complete the verification step to securely access your workspace.'
              : 'Authenticate to deploy agents, view findings, and generate reports.'}
          </Typography>

          {/* Tabs */}
          {authStage === 'credentials' ? <Tabs
            value={value}
            onChange={handleChange}
            aria-label="auth tabs"
            sx={{
              width: 'fit-content',
              mb: 2,
              px: 0.5,
              borderRadius: 999,
              backgroundColor: alpha(PRIMARYCOLOR, 0.06),
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                textTransform: 'none',
                minHeight: 36,
                height: 36,
                px: 2,
                borderRadius: 999,
                color: PRIMARYCOLOR,
                fontWeight: 700
              },
              '& .Mui-selected': {
                color: '#fff !important',
                backgroundColor: PRIMARYCOLOR
              }
            }}
          >
            <LinkTab label="Login" href="/" />
            <LinkTab label="Sign Up" href="#" />
          </Tabs> : null}

          {/* Alerts */}
          {alert && authStage === 'credentials' && (
            <Alert
              sx={{ mb: 2 }}
              icon={alert.type === 'success' ? <CheckIcon fontSize="inherit" /> : undefined}
              severity={alert.type}
            >
              {alert.message}
            </Alert>
          )}

          {/* Auth form panel */}
          <Box
            sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${alpha(PRIMARYCOLOR, 0.15)}`,
              background: 'linear-gradient(180deg, rgba(255,255,255,.92), rgba(255,255,255,.86))',
              boxShadow: `0 10px 30px ${alpha(PRIMARYCOLOR, 0.12)}`
            }}
          >
            {authStage === 'mfa' ? (
              <MfaVerificationForm
                code={mfaCode}
                emailHint={mfaChallenge?.emailHint}
                error={mfaError}
                expiresIn={mfaExpiresIn}
                resendIn={resendAvailableIn}
                verifying={mfaVerifying}
                resending={mfaResending}
                onCodeChange={setMfaCode}
                onVerify={handleMfaVerify}
                onResend={handleMfaResend}
                onBack={() => returnToLogin()}
              />
            ) : !isSignUp ? (
              <LoginForm
                email={email}
                password={password}
                setEmail={setEmail}
                setPassword={setPassword}
                handleModalOpen={handleModalOpen}
                handleLogin={handleLogin}
                loading={loginLoading}
              />
            ) : (
              <SignupForm
                setEmail={setEmail}
                setPassword={setPassword}
                handleModalOpen={handleModalOpen}
                handleSignup={handleSignup}
              />
            )}
          </Box>

          {/* tiny footer */}
          <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
            <Typography variant="caption" color="text.secondary">Protected by AI Agents</Typography>
            <Box sx={{ width: 4, height: 4, bgcolor: PRIMARYCOLOR, borderRadius: '50%' }} />
            <Link underline="hover" variant="caption" href="/privacy">Privacy</Link>
            <Link underline="hover" variant="caption" href="/terms">Terms</Link>
          </Stack>
        </Box>
      </Card>

      {/* bottom corner watermark */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          color: alpha(PRIMARYCOLOR, 0.7),
          fontSize: 12,
          letterSpacing: 1.2
        }}
      >
        © {new Date().getFullYear()} {COMPANY}
      </Box>

      <ForgotPasswordModal
        openModal={openModal}
        handleModalClose={handleModalClose}
        setEmail={setEmail}
        handleForgotPassword={handleForgotPassword}
      />
    </Box>
  );
}

/** ——— chip styles ——— */
function chipWhite() {
  return {
    bgcolor: 'rgba(255,255,255,.14)',
    border: '1px solid rgba(255,255,255,.35)',
    color: '#fff',
    height: 28,
    '& .MuiChip-icon': { color: '#fff !important' }
  };
}

function chipTranslucent() {
  return {
    bgcolor: 'rgba(255,255,255,.12)',
    border: '1px solid rgba(255,255,255,.32)',
    color: '#fff',
    '& .MuiChip-label': { fontWeight: 700, letterSpacing: .4 }
  };
}
